/**
 * Banners carousel component
 *
 * @module app/components/banners/BannersCarousel
 */
import DeviceDetector from '../DeviceDetector';
import Blazy from 'blazy';
import jQuery from 'jquery';
import Logger from '../Logger';
import MessageBusTopics from '../../../common/MessageBusTopics';
import Peppermint from '../../../vendor/peppermint.carousel/peppermint';

/**
 * Used as an ID for each carousel instance
 * @type {Number}
 */
let bannerCount = 0;

/**
 * Dom selectors
 * @type {Object}
 */
const selectors = {
    container: '.banners-container',
    bannersWebSegment: '.forWeb',
    bannersNativeSegment: '.forNative',
    carousel: '.banners-carousel',
    carouselItems: 'article',
    linkButtons: '.banners-carousel__link',
};

/**
 * Initialise a new carousel given an element ID
 *
 * @arg {HTMLElement} container Dom element to instance
 *
 * @returns {BannersCarousel} a new BannersCarousel instance
 *
 * @constructor
 *
 * @public
 */
const BannersCarousel = function (container) {
    this.id = bannerCount;
    container.setAttribute('data-banner-carousel', this.id);
    bannerCount++; // Give each new instance a unique serial
    this.log = Logger.getLogger();
    this.messageBus = window.WH.messageBus;
    this.slideInterval = 5000; // the time to remain on each slide
    this.carousel = null; // peppermint carousel object
    this.dom = {
        container: container,
        carousel: null,
        carouselItems: [],
        linkButtons: jQuery(),
    };

    this.setupDOM();

    if (this.dom.carouselItems.length) {
        this.subscribeForEvents();
        this.initCarousel();
        this.bindLinkButtonClickEvent();
    }
    this.initLazyLoad();
};

BannersCarousel.prototype.initLazyLoad = function () {

    const logger = this.log;
    const container = this.dom.container;
    this.blazy = new Blazy({
        root: container,
        loadInvisible: false,
        success: function () {
            container.classList.remove('banners-container--loading');
        },
        error: function (element, msg) {
            element.classList.remove('b-lazy');
            logger(element, msg);
        },
    });
};
/**
 * Return an event handler that wraps and executes an action if it is for the same
 * carousel instance
 *
 * @param  {Function} fn The original event handler
 *
 * @return {Function} Event handler wrapper function
 */
BannersCarousel.prototype.scopeActionBinding = function (fn) {

    return function (eventName, action) {
        if (action.carouselId === this.id) {
            fn(eventName, action);
        }
    }.bind(this);
};

/**
 * Decorate a dispatched event payload with the carousel id so that we are able to
 * distinguish the origin carousel that emitted the event
 *
 * @param  {Object} payload Generic payload object
 *
 * @return {Object}         Decorated payload object
 *
 * @public
 */
BannersCarousel.prototype.scopeAction = function (payload) {

    return {
        carouselId: this.id,
        payload: payload,
    };
};

/**
 * Destroy carousel.
 *
 * @returns {void}
 *
 * @public
 */
BannersCarousel.prototype.destroy = function () {

    if (this.carousel !== null && !document.body.contains(this.dom.container)) {
        this.carousel.stop();
        this.carousel = null;
    }
};

/**
 * Handle all subscriptions
 *
 * @return {void}
 *
 * @public
 */
BannersCarousel.prototype.subscribeForEvents = function () {

    this.messageBus.subscribe(
        MessageBusTopics.BANNERS_CAROUSEL_LINK_CLICKED, this.scopeActionBinding(this.goToSlide.bind(this))
    );
    this.messageBus.subscribe(
        MessageBusTopics.BANNERS_CAROUSEL_LINK_CLICKED, this.scopeActionBinding(this.stopAuto.bind(this))
    );
    this.messageBus.subscribe(
        MessageBusTopics.BANNERS_CAROUSEL_INITIALISED,
        this.scopeActionBinding(this.resetSlideStyles.bind(this))
    );
    this.messageBus.subscribe(
        MessageBusTopics.BANNERS_CAROUSEL_SLIDE_CHANGED,
        (eventName, action) => {
            this.prefetchImage(action.payload);
            this.scopeActionBinding(this.setActiveLinkButton.bind(this))(eventName, action);
        }
    );
};

/**
 * Set DOM objects if a carousel exists.
 *
 * @returns {void}
 *
 * @public
 */
BannersCarousel.prototype.setupDOM = function () {
    try {
        if (DeviceDetector.isNative() && DeviceDetector.isIOS()) {
            this.dom.segment = this.dom.container.querySelector(selectors.bannersNativeSegment);
        } else {
            this.dom.segment = this.dom.container.querySelector(selectors.bannersWebSegment);
        }

        this.dom.linkButtons = jQuery(this.dom.segment.querySelectorAll(selectors.linkButtons));
        this.dom.carousel = this.dom.segment.querySelector(selectors.carousel);
        this.dom.carouselItems = this.dom.carousel.querySelectorAll(selectors.carouselItems);
    } catch (e) {
        this.log('Error processing banner for id: ' + this.id + ', message: ' + e.message);
    }
};

/**
 * Bind the click event handler for link buttons.
 *
 * @returns {void}
 *
 * @public
 */
BannersCarousel.prototype.bindLinkButtonClickEvent = function () {

    this.dom.linkButtons.off().on('click', function (e) {
        e.preventDefault();
        this.messageBus.publish(MessageBusTopics.BANNERS_CAROUSEL_LINK_CLICKED, this.scopeAction(e.target));
    }.bind(this));
};

// carousel stuff

/**
 * Initialise carousel.
 *
 * @returns {void}
 *
 * @public
 */
BannersCarousel.prototype.initCarousel = function () {
    const carousel = this.carousel = new Peppermint(this.dom.carousel, {
        dots: false,
        slideshow: false,
        speed: 500,
        slideshowInterval: this.slideInterval,
        stopSlideshowAfterInteraction: true,
        onSlideChange: (n) => {
            this.messageBus.publish(MessageBusTopics.BANNERS_CAROUSEL_SLIDE_CHANGED, this.scopeAction(n));
        },
        onSetup: () => {
            this.messageBus.publish(MessageBusTopics.BANNERS_CAROUSEL_INITIALISED, this.scopeAction());
        },
    });

};

BannersCarousel.prototype.prefetchImage = function(imageIndex) {
    const carouselItem = this.dom.carouselItems[imageIndex];
    if (carouselItem) {
        const carouselImage = carouselItem.querySelector('img');
        this.blazy.load(carouselImage, true);
    }
};

/**
 * Go to a specific slide.
 *
 * @param {String} eventName Event Name
 * @param {Object} action Action
 *
 * @returns {void}
 *
 * @public
 */
BannersCarousel.prototype.goToSlide = function (eventName, action) {
    const linkButtonObject = jQuery(action.payload);

    if (!linkButtonObject.hasClass('banners-carousel__link--active')) {
        const buttonIndex = linkButtonObject.index();
        this.carousel.slideTo(buttonIndex);
    }
};

/**
 * Stop auto sliding.
 *
 * @returns {void}
 *
 * @public
 */
BannersCarousel.prototype.stopAuto = function () {

    this.carousel.stop();
};

// dom stuff

/**
 * To prevent seeing the slides appearing stacked before the carousel
 * kicks in we hide all slides except the first using display none.
 * To get the slides to display correctly again we set them back to display block.
 *
 * @returns {void}
 *
 * @public
 */
BannersCarousel.prototype.resetSlideStyles = function () {

    for (let i = 0; i < this.dom.carouselItems.length; ++i) {
        this.dom.carouselItems[i].style.display = 'block';
    }
};

/**
 * Add active class to banner link based on current position of carousel.
 *
 * @param {String} eventName Name of event
 * @param {Object} action Action object for the current instance
 *
 * @returns {void}
 *
 * @public
 */
BannersCarousel.prototype.setActiveLinkButton = function (eventName, action) {
    this.dom.linkButtons.removeClass('banners-carousel__link--active');
    this.dom.linkButtons
        .filter(function (index) {
            return index === action.payload;
        })
        .addClass('banners-carousel__link--active');
};

/**
 * Public facing API
 * @type {Object}
 */
const api = {
    init: function () {
        if (this.initialized) {
            const containers = Array.from(document.querySelectorAll(selectors.container));

            const createBannerCarousel = function (container) {
                if (!container.hasAttribute('data-banner-carousel')) {
                    const carousel = new BannersCarousel(container);
                    this.instances[carousel.id] = carousel;
                }
            };
            containers.forEach(createBannerCarousel, this);
        } else {

            this.initialized = !this.initialized;
            window.WH.messageBus.subscribe(
                MessageBusTopics.DETECTION_MARKUP_INIT_COMPLETE, this.init.bind(this)
            );
            window.WH.messageBus.subscribe(
                MessageBusTopics.NAVIGATION_PAGE_CHANGED, this.init.bind(this)
            );
            window.WH.messageBus.subscribe(
                MessageBusTopics.NAVIGATION_BEFORE_PAGE_CHANGE, this.destroyCarousels.bind(this)
            );
        }
    },
    destroyCarousels: function () {

        Object.keys(this.instances).forEach(function (key) {
            this.instances[key].destroy();
            delete this.instances[key];
        }.bind(this));
    },
    initialized: false,
    instances: {},
};

export default api;
